import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TelegramLink } from "../../common/TelegramLink";
import { QueryService } from "../../../services/QueryService";
import {
  AfishaApiEvent,
  AfishaEvent,
  buildDayMeta,
  getTodayDateStr,
  mapApiEventsToDayEvents,
  normalizeDateStr,
} from "./afishaUtils";
import { AfishaTrack } from "./AfishaTrack";
import {
  markAfishaBootSuccess,
  reportAfishaLoadError,
} from "../../../services/clientAlert";
import "./AfishaPage.scss";

type ChannelInfo = {
  name: string;
  title: string;
  info: string;
  imgUrl: string | null;
  tgUrl: string | null;
};

type ChannelAfishaInitResponse = {
  type: string;
  data: {
    channel: ChannelInfo;
    dates: string[];
    activeDate: string;
    events: AfishaApiEvent[];
  };
};

type ChannelAfishaDayResponse = {
  type: string;
  data: {
    date: string;
    events: AfishaApiEvent[];
  };
};

const CopyIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const getChannelPageUrl = (channelName: string): string =>
  `${window.location.origin}/channels/${encodeURIComponent(channelName)}`;

const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
};

const ChannelHeaderAvatar = ({ channel }: { channel: ChannelInfo }) => {
  const [imgError, setImgError] = useState(false);
  const imgSrc = channel.imgUrl
    ? `${QueryService.getServerUri()}${channel.imgUrl}`
    : null;

  if (!imgSrc || imgError) {
    return (
      <span className="afisha-channel-page__avatar afisha-channel-page__avatar--placeholder">
        {channel.title.charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      className="afisha-channel-page__avatar"
      src={imgSrc}
      alt=""
      loading="lazy"
      onError={() => setImgError(true)}
    />
  );
};

export const AfishaChannelPage = () => {
  const { name = "" } = useParams<{ name: string }>();
  const [channel, setChannel] = useState<ChannelInfo | null>(null);
  const [dates, setDates] = useState<string[]>([]);
  const [activeDate, setActiveDate] = useState("");
  const [eventsByDate, setEventsByDate] = useState<Record<string, AfishaEvent[]>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [loadingDay, setLoadingDay] = useState(false);
  const [error, setError] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  const loadDayEvents = useCallback(
    async (date: string) => {
      if (!name) return;

      setLoadingDay(true);
      try {
        const resp = await QueryService.postAfishaQuery(
          `/api/channels/${encodeURIComponent(name)}/afisha`,
          { date },
        );
        const payload = resp?.data as ChannelAfishaDayResponse | undefined;
        const events = mapApiEventsToDayEvents(payload?.data?.events ?? []);
        setEventsByDate((prev) => ({ ...prev, [date]: events }));
      } catch {
        setError("Не удалось загрузить события");
      } finally {
        setLoadingDay(false);
      }
    },
    [name],
  );

  useEffect(() => {
    if (!name) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      setChannel(null);
      setDates([]);
      setEventsByDate({});
      setActiveDate("");

      try {
        const resp = await QueryService.postAfishaQuery(
          `/api/channels/${encodeURIComponent(name)}/afisha`,
        );
        const payload = resp?.data as ChannelAfishaInitResponse | undefined;
        if (cancelled) return;

        const activeDate = normalizeDateStr(
          payload?.data?.activeDate ?? getTodayDateStr(),
        );
        const initialEvents = mapApiEventsToDayEvents(
          payload?.data?.events ?? [],
        );

        setChannel(payload?.data?.channel ?? null);
        setDates(
          (payload?.data?.dates ?? [])
            .map((date) => normalizeDateStr(date))
            .filter(Boolean),
        );
        setActiveDate(activeDate);
        setEventsByDate({ [activeDate]: initialEvents });
      } catch {
        if (!cancelled) {
          const loadError = "Не удалось загрузить афишу канала";
          setError(loadError);
          reportAfishaLoadError(loadError);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [name]);

  useEffect(() => {
    if (!channel?.title) return;
    document.title = `${channel.title} — КудаВГород`;
  }, [channel?.title]);

  useEffect(() => {
    if (!loading && !error && channel) {
      markAfishaBootSuccess();
    }
  }, [loading, error, channel]);

  const handleDateClick = async (date: string) => {
    setActiveDate(date);
    if (eventsByDate[date] !== undefined) return;
    await loadDayEvents(date);
  };

  const dayMeta = useMemo(
    () => dates.map((date) => buildDayMeta(date)),
    [dates],
  );

  const events = eventsByDate[activeDate];
  const isDayLoading = loadingDay && events === undefined;

  const handleCopyChannelLink = async () => {
    if (!channel?.name) return;

    const ok = await copyToClipboard(getChannelPageUrl(channel.name));
    if (!ok) return;

    setLinkCopied(true);
    window.setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div className="afisha-channel-page">
      {loading && <p className="afisha__status">Загрузка…</p>}
      {error && <p className="afisha__status afisha__status--error">{error}</p>}

      {!loading && !error && channel && (
        <>
          <div className="afisha-channel-page__header">
            <ChannelHeaderAvatar channel={channel} />
            <div className="afisha-channel-page__title-row">
              <h1 className="afisha-channel-page__title">{channel.title}</h1>
              <button
                type="button"
                className={
                  "afisha-channel-page__copy-link" +
                  (linkCopied ? " afisha-channel-page__copy-link--copied" : "")
                }
                onClick={() => void handleCopyChannelLink()}
                aria-label={
                  linkCopied
                    ? "Ссылка скопирована"
                    : "Скопировать ссылку на канал"
                }
                title={
                  linkCopied
                    ? "Ссылка скопирована"
                    : "Скопировать ссылку на канал"
                }
              >
                {linkCopied ? <CheckIcon /> : <CopyIcon />}
              </button>
            </div>
            {channel.info ? (
              <p className="afisha-channel-page__info">{channel.info}</p>
            ) : null}
            {channel.tgUrl ? (
              <TelegramLink
                className="afisha-channel-page__tg-link"
                href={channel.tgUrl}
              >
                @{channel.name}
              </TelegramLink>
            ) : null}
          </div>

          {dates.length > 0 ? (
            <>
              <div className="afisha__dates" role="tablist" aria-label="Даты">
                {dayMeta.map((day) => {
                  const isActive = day.date === activeDate;
                  return (
                    <button
                      key={day.date}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={
                        "afisha__date" +
                        (isActive ? " afisha__date--active" : "")
                      }
                      onClick={() => handleDateClick(day.date)}
                    >
                      <span className="afisha__date-weekday">
                        {day.weekday}
                      </span>
                      <span className="afisha__date-num">{day.dayNum}</span>
                    </button>
                  );
                })}
              </div>

              <div className="afisha__tracks" aria-live="polite">
                {isDayLoading ? (
                  <p className="afisha__status">Загрузка…</p>
                ) : events && events.length > 0 ? (
                  events.map((event) => (
                    <AfishaTrack
                      key={`${activeDate}-${event.id}`}
                      event={event}
                    />
                  ))
                ) : events ? (
                  <p className="afisha__empty">На этот день событий пока нет</p>
                ) : null}
              </div>
            </>
          ) : (
            <p className="afisha__empty afisha-channel-page__empty">
              У канала пока нет запланированных мероприятий
            </p>
          )}

          <div className="afisha-channel-page__back">
            <Link to="/channels" className="afisha-channel-page__back-link">
              ← Все каналы
            </Link>
          </div>
        </>
      )}
    </div>
  );
};
